import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const EarningsChart = React.memo(function EarningsChart({
  data = [],
  timeFilter = '30d',
  onTimeFilterChange,
  isArabic
}) {
  const translations = {
    title: { en: 'Earnings History', ar: 'سجل الأرباح' },
    subtitle: { en: 'Overview of your income over the last 30 days.', ar: 'نظرة عامة على دخلك خلال آخر 30 يوماً.' },
    filters: {
      '7d': { en: 'Last 7 Days', ar: 'آخر 7 أيام' },
      '30d': { en: 'Last 30 Days', ar: 'آخر 30 يوماً' },
      '90d': { en: 'Last 90 Days', ar: 'آخر 90 يوماً' },
      '1y': { en: 'This Year', ar: 'هذا العام' }
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      return (
        <div className="bg-white px-3 py-2 rounded-lg shadow-md border border-gray-200">
          <p className="text-sm font-semibold text-gray-900">
            {new Intl.NumberFormat(isArabic ? 'ar-BH' : 'en-BH', {
              style: 'currency',
              currency: 'BHD',
              minimumFractionDigits: 2
            }).format(value)}
          </p>
          {payload[0].payload.date && (
            <p className="text-xs text-gray-500">
              {payload[0].payload.date}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100"
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div className="mb-4 md:mb-0">
          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1">
            {translations.title[isArabic ? 'ar' : 'en']}
          </h3>
          <p className="text-sm text-gray-500">
            {translations.subtitle[isArabic ? 'ar' : 'en']}
          </p>
        </div>

        {/* Time Filter Dropdown */}
        {onTimeFilterChange && (
          <select
            value={timeFilter}
            onChange={(e) => onTimeFilterChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
          >
            {Object.entries(translations.filters).map(([key, value]) => (
              <option key={key} value={key}>
                {value[isArabic ? 'ar' : 'en']}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Chart */}
      <div className="w-full" style={{ height: '300px' }}>
        {data && data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{
                top: 5,
                right: isArabic ? 5 : 5,
                left: isArabic ? 5 : 0,
                bottom: 5
              }}
            >
              <defs>
                <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF7A1A" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#FF7A1A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="day"
                stroke="#9CA3AF"
                style={{ fontSize: '12px' }}
                reversed={isArabic}
                padding={{ left: 0, right: 0 }}
              />
              <YAxis
                stroke="#9CA3AF"
                style={{ fontSize: '12px' }}
                width={25}
                tickFormatter={(value) =>
                  new Intl.NumberFormat(isArabic ? 'ar-BH' : 'en-BH', {
                    notation: 'compact',
                    compactDisplay: 'short'
                  }).format(value)
                }
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="earnings"
                stroke="#FF7A1A"
                strokeWidth={2}
                dot={false}
                activeDot={false}
                fill="url(#colorEarnings)"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-400 text-sm">
              {isArabic ? 'لا توجد بيانات لعرضها' : 'No data to display'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

export default EarningsChart;
